import { appApi } from '.';
import { push } from 'connected-react-router';
import qs from 'query-string';
import { tunnelSchemas } from 'schemas';
import {
    RoleNames,
    UserEnrichments,
    docTypes,
    fundTypes,
    investorClassificationKeys,
    skippableStepsForProClient,
    stepIDs,
    stepStatus,
    subscriptionDocStatus,
} from 'utils/constants';
import { tunnelActions, tunnelSelectors, userSelectors } from 'reducers';
import { authApi, documentApi } from './main';
import {
    codeVerifierSet,
    subscriptionFormStatusSet,
    tunnelSubscriptionUpdated,
    userTunnelStepsUpdated,
} from 'reducers/user';
import { checkFormDataHasFiles } from 'utils/helpers';

const {
    INVESTOR_PROFILE,
    BANKING_STATEMENT,
    USER_TUNNEL_STEPS,
    INVESTMENT_CHARACTERISTICS,
    INVESTMENT_FUND,
    USER_ADEQUACY,
    COMPANY,
    DOCUMENTS,
    KYC_SURVEY,
    SIGNATURES,
    SUBSCRIPTIONS,
    ROLES,
    DISTRIBUTOR_COMPANY,
    ALERTS,
    USER_KNOWLEDGE,
    SHARE_SUBCATEGORY,
} = UserEnrichments;

const docVerificationEnrichments = [
    INVESTOR_PROFILE,
    USER_TUNNEL_STEPS,
    SUBSCRIPTIONS,
    ALERTS,
    DOCUMENTS,
    COMPANY,
    KYC_SURVEY,
    USER_KNOWLEDGE,
];

const tunnelApi = appApi.injectEndpoints({
    endpoints: build => ({
        sendInvestorProfile: build.mutation({
            async queryFn(
                { resubmit, callback, ...payload },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) {
                dispatch(tunnelActions.setTunnelErrors());
                const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                const isPP = tunnelSubscription?.role?.name === RoleNames.PP_PROFESSIONNEL;
                const isPM = tunnelSubscription?.role?.name === RoleNames.PM_PROFESSIONNEL;
                let requestPayload;

                if (isPP) {
                    requestPayload = tunnelSchemas.sendInvestorProfilePP(payload);
                } else if (isPM) {
                    const legalRepresentativeDocPayload =
                        tunnelSchemas.legalRepresentativeDocPayload(payload);
                    let legalRepresentativeDocId = null;
                    if (legalRepresentativeDocPayload) {
                        //upload and retrieve doc id
                        legalRepresentativeDocId = await documentApi.sendDocuments(
                            legalRepresentativeDocPayload
                        );
                    }
                    requestPayload = tunnelSchemas.sendInvestorProfilePM(
                        payload,
                        legalRepresentativeDocId
                    );
                }

                const response = await fetchWithBQ({
                    url: '/register/3',
                    method: 'POST',
                    data: requestPayload,
                });

                // update tunnel list in store
                // the tunnel steps have to be updated before advancing in the tunnel when the `withCoSubscriber` option is changed
                // this would include or exclude the co-subscriber data step which would be right after this current step
                if (
                    isPP &&
                    requestPayload.subscriptions[0].withCoSubscriber !==
                        tunnelSubscription.widthCoSubscriber
                ) {
                    const tunnelsQueryObject = {
                        investmentFunds: tunnelSubscription?.investmentFund?.id,
                        role: tunnelSubscription?.role?.id,
                    };
                    const tunnelsResponse = await fetchWithBQ({
                        url: `/public/tunnels?${qs.stringify({
                            ...tunnelsQueryObject,
                            timestamp: Date.now(),
                        })}`,
                    });
                    dispatch(
                        appApi.util.updateQueryData(
                            'getTunnels',
                            tunnelsQueryObject,
                            () => tunnelsResponse.data
                        )
                    );
                }

                const user = await authApi.getMeEnriched({
                    enrich_with: [
                        INVESTOR_PROFILE,
                        USER_TUNNEL_STEPS,
                        KYC_SURVEY,
                        SUBSCRIPTIONS,
                        ALERTS,
                        DOCUMENTS,
                        COMPANY,
                        USER_KNOWLEDGE,
                        INVESTMENT_CHARACTERISTICS,
                    ],
                });

                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));
                if (
                    isPP &&
                    requestPayload.subscriptions[0].withCoSubscriber &&
                    (tunnelSubscription.indirectUntransferred || resubmit)
                ) {
                    // this is needed because the tunnel list changes do not propagate fast enough to navigate automatically to this step
                    // (that is not previously in the list)
                    dispatch(push(`/sign-up/${stepIDs.CO_SUBSCRIBER_CONTACT_INFO}`));
                } else if (!response.error) {
                    // the submit handler was called from outside the component step
                    // (i.e. the navigation buttons were used while having unsaved changes on the step form)
                    dispatch(
                        codeVerifierSet(!resubmit && !tunnelSubscription.indirectUntransferred)
                    );
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
            invalidatesTags: ['Tunnel'],
        }),

        sendKnowClient: build.mutation({
            queryFn: async (
                { resubmit, callback, ...payload },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                const isPP = tunnelSubscription?.role?.name === RoleNames.PP_PROFESSIONNEL;

                let requestPayload = null;
                if (isPP) {
                    requestPayload = tunnelSchemas.sendKnowClientPP(payload);
                } else {
                    requestPayload = tunnelSchemas.sendKnowClientPM(payload);
                }

                const response = await fetchWithBQ({
                    url: '/register/8',
                    method: 'POST',
                    data: requestPayload,
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: [
                        USER_TUNNEL_STEPS,
                        KYC_SURVEY,
                        USER_ADEQUACY,
                        SUBSCRIPTIONS,
                        ALERTS,
                        DOCUMENTS,
                        USER_KNOWLEDGE,
                        INVESTMENT_CHARACTERISTICS,
                    ],
                });
                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));
                callback?.();

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        startIdVerification: build.mutation({
            query: () => ({
                url: '/register/12/1',
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['me'],
        }),

        sendIdVerification: build.mutation({
            queryFn: async ({ callback, stepId }, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: `/register/${stepId}/2`,
                    method: 'POST',
                    data: {},
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: docVerificationEnrichments,
                });
                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));
                callback?.();

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        startSecondIdVerification: build.mutation({
            query: () => ({
                url: '/register/21/1',
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['me'],
        }),

        startAddressVerification: build.mutation({
            query: payload => ({
                url: '/register/13/1',
                method: 'POST',
                data: tunnelSchemas.startAddressVerification(payload),
            }),
            invalidatesTags: ['me'],
        }),

        sendAddressVerification: build.mutation({
            queryFn: async (callback, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/13/2',
                    method: 'POST',
                    data: {},
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: docVerificationEnrichments,
                });
                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));
                callback?.();

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendBankAccount: build.mutation({
            queryFn: async (
                { callback, payload },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                dispatch(tunnelActions.setTunnelErrors());
                const response = await fetchWithBQ({
                    url: '/register/14',
                    method: 'POST',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    data: tunnelSchemas.sendBankAccount(payload),
                });

                if (response.error) {
                    callback?.(response.error);
                } else {
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());

                    await dispatch(
                        appApi.endpoints.getTunnels.initiate(
                            {
                                investmentFunds: tunnelSubscription?.investmentFund?.id,
                                role: tunnelSubscription?.role?.id,
                            },
                            { forceRefetch: true }
                        )
                    );

                    const user = await authApi.getMeEnriched({
                        enrich_with: [
                            INVESTOR_PROFILE,
                            COMPANY,
                            INVESTMENT_CHARACTERISTICS,
                            INVESTMENT_FUND,
                            BANKING_STATEMENT,
                            DOCUMENTS,
                            SUBSCRIPTIONS,
                            USER_TUNNEL_STEPS,
                            ALERTS,
                            KYC_SURVEY,
                            USER_KNOWLEDGE,
                        ],
                    });
                    // at this point the old BS document (if it existed) was deleted
                    // we need to clear the list
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );
                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendRegulationAgreement: build.mutation({
            queryFn: async (payload, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/15',
                    method: 'POST',
                    data: tunnelSchemas.sendRegulationAgreement(payload),
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: docVerificationEnrichments,
                });
                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendDiciAgreement: build.mutation({
            queryFn: async (payload, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/16',
                    method: 'POST',
                    data: tunnelSchemas.sendDiciAgreement(payload),
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: docVerificationEnrichments,
                });
                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));

                return response.error ? { error: response.error } : { data: null };
            },
            keepUnusedDataFor: 0,
        }),

        getDDASigningSession: build.mutation({
            query: () => ({
                url: '/register/17/1',
                method: 'POST',
                data: {},
            }),
        }),

        completeDDASigningSession: build.mutation({
            queryFn: async (callback, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/17/2',
                    method: 'POST',
                    data: {},
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: [
                        USER_TUNNEL_STEPS,
                        SUBSCRIPTIONS,
                        ALERTS,
                        COMPANY,
                        DOCUMENTS,
                        KYC_SURVEY,
                        USER_KNOWLEDGE,
                    ],
                });
                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));
                callback?.();

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        generateSubscriptionForm: build.mutation({
            queryFn: async (_payload, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/18/1',
                    method: 'POST',
                    data: {},
                });

                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: [
                            INVESTOR_PROFILE,
                            USER_TUNNEL_STEPS,
                            SIGNATURES,
                            SUBSCRIPTIONS,
                            ALERTS,
                            COMPANY,
                            DISTRIBUTOR_COMPANY,
                            DOCUMENTS,
                        ],
                    });

                    dispatch(subscriptionFormStatusSet(subscriptionDocStatus.GENERATING));
                    dispatch(tunnelSubscriptionUpdated(user));
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        getSubscriptionSigningSession: build.mutation({
            queryFn: async (_payload, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/18/2',
                    method: 'POST',
                    data: {},
                });
                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: [
                            USER_TUNNEL_STEPS,
                            SUBSCRIPTIONS,
                            SIGNATURES,
                            ALERTS,
                            COMPANY,
                            DOCUMENTS,
                            KYC_SURVEY,
                            USER_KNOWLEDGE,
                        ],
                    });
                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        completeSubscriptionSigningSession: build.mutation({
            queryFn: async (_payload, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/18/3',
                    method: 'POST',
                    data: {},
                });
                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: [
                            INVESTOR_PROFILE,
                            USER_TUNNEL_STEPS,
                            SIGNATURES,
                            SUBSCRIPTIONS,
                            ALERTS,
                            DOCUMENTS,
                            COMPANY,
                        ],
                    });
                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        completeCoSubscriptionSigningSession: build.mutation({
            queryFn: async (_payload, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/18/4',
                    method: 'POST',
                    data: {},
                });
                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: [
                            ALERTS,
                            COMPANY,
                            DOCUMENTS,
                            INVESTOR_PROFILE,
                            SIGNATURES,
                            SUBSCRIPTIONS,
                            USER_TUNNEL_STEPS,
                        ],
                    });
                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        finishDistributorTunnel: build.mutation({
            query: () => ({ url: '/register/19', method: 'POST' }),
        }),

        sendDistributorDocuments: build.mutation({
            queryFn: async ({ callback, payload }, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/32',
                    method: 'POST',
                    data: tunnelSchemas.sendDistributorDocuments(payload),
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: [
                        COMPANY,
                        USER_TUNNEL_STEPS,
                        SUBSCRIPTIONS,
                        BANKING_STATEMENT,
                        DISTRIBUTOR_COMPANY,
                        ALERTS,
                        DOCUMENTS,
                    ],
                });
                dispatch(tunnelSubscriptionUpdated(user));
                if (!response.error) {
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendSurveyResults: build.mutation({
            queryFn: async ({ callback }, { dispatch, getState }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/38',
                    method: 'POST',
                    data: {},
                });

                if (!response.error) {
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());

                    // at this point the old BS document (if it existed) was deleted
                    // we need to clear the list
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    const user = await authApi.getMeEnriched({
                        enrich_with: [
                            INVESTOR_PROFILE,
                            USER_ADEQUACY,
                            USER_TUNNEL_STEPS,
                            KYC_SURVEY,
                            ALERTS,
                            SUBSCRIPTIONS,
                            DOCUMENTS,
                            COMPANY,
                            USER_KNOWLEDGE,
                            INVESTMENT_CHARACTERISTICS,
                        ],
                    });
                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        confirmKYCPedagogicalStep: build.mutation({
            queryFn: async (_, { dispatch }, _extraOptions, fetchWithBQ) => {
                const response = await fetchWithBQ({
                    url: '/register/33',
                    method: 'POST',
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: [
                        USER_TUNNEL_STEPS,
                        SUBSCRIPTIONS,
                        ALERTS,
                        COMPANY,
                        KYC_SURVEY,
                        USER_KNOWLEDGE,
                        DOCUMENTS,
                        INVESTMENT_CHARACTERISTICS,
                    ],
                });
                dispatch(userTunnelStepsUpdated(user));

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendInvestorEligibility: build.mutation({
            queryFn: async (
                { payload, callback },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                const isCompany = tunnelSubscription?.role?.name === RoleNames.PM_PROFESSIONNEL;
                const { type } = tunnelSelectors.getCurrentInvestmentFund(getState());
                const isFPCI = type === fundTypes.FPCI;
                const isFPS = type === fundTypes.FPS;
                const isSLP = type === fundTypes.SLP;

                const userTunnelSteps = tunnelSubscription?.userTunnelSteps;
                const validatedStepsWithoutData = userTunnelSteps
                    .filter(
                        uts =>
                            uts.status === stepStatus.VERIFIED &&
                            uts.successfulAttemptsNumber === 0 &&
                            skippableStepsForProClient.includes(uts.tunnelStep.step.id)
                    )
                    .map(uts => uts.tunnelStep.step.id);

                const [
                    balanceSheetTotalDocs,
                    netSalesOrRevenuesDocs,
                    higherEquityDocs,
                    exemptionConditionsProofDocs,
                ] = tunnelSchemas.sendInvestorEligibilityDocs(payload);
                const [
                    transactionsCompletedDocs,
                    portfolioValueDocs,
                    professionalPositionHeldDocs,
                ] = tunnelSchemas.sendSurveyResultsDocs(payload);

                const necessaryEnrichments = [
                    INVESTMENT_CHARACTERISTICS,
                    SHARE_SUBCATEGORY,
                    USER_TUNNEL_STEPS,
                    SUBSCRIPTIONS,
                    ROLES,
                    ALERTS,
                    INVESTMENT_FUND,
                    SUBSCRIPTIONS,
                    INVESTOR_PROFILE,
                    COMPANY,
                    USER_ADEQUACY,
                    KYC_SURVEY,
                    USER_KNOWLEDGE,
                    DOCUMENTS,
                ];

                // handle documents from userAdequacy
                let savedProfessionalPositionHeldDocs = [];
                let savedPortfolioValueDocs = [];
                let savedTransactionsCompletedDocs = [];

                if (professionalPositionHeldDocs) {
                    savedProfessionalPositionHeldDocs = await documentApi.sendDocuments(
                        professionalPositionHeldDocs
                    );
                }
                if (portfolioValueDocs) {
                    savedPortfolioValueDocs = await documentApi.sendDocuments(portfolioValueDocs);
                }
                if (transactionsCompletedDocs) {
                    savedTransactionsCompletedDocs = await documentApi.sendDocuments(
                        transactionsCompletedDocs
                    );
                }
                const [documentsToDelete, surveyResultPayload] = tunnelSchemas.sendSurveyResults(
                    payload,
                    savedTransactionsCompletedDocs,
                    savedPortfolioValueDocs,
                    savedProfessionalPositionHeldDocs
                );
                if (documentsToDelete && documentsToDelete.length > 0) {
                    await documentApi.removeDocuments(documentsToDelete);
                }

                // handle documents from company form
                if (payload.filesToRemove?.length > 0) {
                    await documentApi.removeDocuments(payload.filesToRemove);
                }

                let savedBalanceSheetTotalDocs = [];
                let savedNetSalesOrRevenuesDocs = [];
                let savedHigherEquityDocs = [];
                let savedExemptionConditionsProofDocs = [];
                if (balanceSheetTotalDocs) {
                    savedBalanceSheetTotalDocs = await documentApi.sendDocuments(
                        balanceSheetTotalDocs
                    );
                }
                if (netSalesOrRevenuesDocs) {
                    savedNetSalesOrRevenuesDocs = await documentApi.sendDocuments(
                        netSalesOrRevenuesDocs
                    );
                }
                if (higherEquityDocs) {
                    savedHigherEquityDocs = await documentApi.sendDocuments(higherEquityDocs);
                }
                if (exemptionConditionsProofDocs) {
                    savedExemptionConditionsProofDocs = await documentApi.sendDocuments(
                        exemptionConditionsProofDocs
                    );
                }

                const eligibilityPayload = tunnelSchemas.sendInvestorEligibility(
                    { ...payload, ...surveyResultPayload.subscriptions[0] },
                    savedBalanceSheetTotalDocs,
                    savedNetSalesOrRevenuesDocs,
                    savedHigherEquityDocs,
                    savedExemptionConditionsProofDocs
                );

                const response = await fetchWithBQ({
                    url: '/register/34',
                    method: 'POST',
                    data: eligibilityPayload,
                });

                if (response.error) {
                    if (
                        response.error.status &&
                        ![400, 401, 403, 500].includes(response.error.status)
                    ) {
                        // handling business errors from server:
                        // * the bad data is still saved in the DB so we need to update the store
                        const user = await authApi.getMeEnriched({
                            enrich_with: necessaryEnrichments,
                        });
                        dispatch(tunnelSubscriptionUpdated(user));
                    }
                } else if (!response.error) {
                    if (payload.exemptionConditionsProofToRemove?.length > 0) {
                        await documentApi.removeDocuments(payload.exemptionConditionsProofToRemove);
                    }

                    if ((isFPCI || isSLP || isFPS) && isCompany) {
                        if (
                            response.data.investorClassification === investorClassificationKeys.PRO
                        ) {
                            // set the following steps [35,36,37,38,8,45] to verified because in this case the user is already professional
                            await fetchWithBQ({
                                url: '/user_tunnel_steps/validate',
                                method: 'POST',
                                data: { validated: true, steps: skippableStepsForProClient },
                            });
                        } else if (validatedStepsWithoutData?.length > 0) {
                            // if this is FPCI_PM and the above steps are verified then set them to incomplete because the user is not professional
                            await fetchWithBQ({
                                url: '/user_tunnel_steps/validate',
                                method: 'POST',
                                data: { validated: false, steps: validatedStepsWithoutData },
                            });
                        }
                    }
                    const user = await authApi.getMeEnriched({ enrich_with: necessaryEnrichments });

                    // we need to clear the BS document list
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendKBIS: build.mutation({
            queryFn: async (
                { payload, callback },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                const necessaryEnrichments = [
                    INVESTOR_PROFILE,
                    COMPANY,
                    SUBSCRIPTIONS,
                    USER_TUNNEL_STEPS,
                    ALERTS,
                    DOCUMENTS,
                    KYC_SURVEY,
                    USER_KNOWLEDGE,
                ];
                const response = await fetchWithBQ({
                    url: '/register/22',
                    method: 'POST',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    data: payload,
                });

                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: necessaryEnrichments,
                    });
                    dispatch(tunnelSubscriptionUpdated(user));

                    // we need to clear the BS document list
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendStatuts: build.mutation({
            queryFn: async (
                { payload, callback },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                const necessaryEnrichments = [
                    ALERTS,
                    COMPANY,
                    DOCUMENTS,
                    INVESTOR_PROFILE,
                    KYC_SURVEY,
                    SUBSCRIPTIONS,
                    USER_KNOWLEDGE,
                    USER_TUNNEL_STEPS,
                ];
                const response = await fetchWithBQ({
                    url: '/register/23',
                    method: 'POST',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    data: payload,
                });

                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: necessaryEnrichments,
                    });
                    dispatch(tunnelSubscriptionUpdated(user));

                    // we need to clear the BS document list
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendInvestorCompany: build.mutation({
            queryFn: async (
                { payload, callback, filesToDelete },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                if (filesToDelete.length) {
                    await documentApi.removeDocuments(filesToDelete);
                }
                const necessaryEnrichments = [
                    COMPANY,
                    USER_TUNNEL_STEPS,
                    SUBSCRIPTIONS,
                    ALERTS,
                    DOCUMENTS,
                    KYC_SURVEY,
                    USER_KNOWLEDGE,
                    INVESTMENT_CHARACTERISTICS,
                ];
                const response = await fetchWithBQ({
                    url: '/register/24',
                    method: 'POST',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    data: payload,
                });

                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: necessaryEnrichments,
                    });
                    dispatch(tunnelSubscriptionUpdated(user));

                    // we need to clear the BS document list
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendCompanyBeneficiaries: build.mutation({
            queryFn: async (
                { payload: { filesToRemove, ...payload }, callback },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                if (filesToRemove?.length > 0) {
                    await documentApi.removeDocuments(filesToRemove);
                }

                let documentIDs;
                if (payload.documents && checkFormDataHasFiles(payload.documents)) {
                    documentIDs = await documentApi.sendDocuments(payload.documents);
                }
                const requestPayload = tunnelSchemas.sendCompanyBeneficiaries(
                    payload.ownerForm,
                    documentIDs
                );
                const necessaryEnrichments = [
                    COMPANY,
                    USER_TUNNEL_STEPS,
                    SUBSCRIPTIONS,
                    ALERTS,
                    KYC_SURVEY,
                    USER_KNOWLEDGE,
                    DOCUMENTS,
                ];
                const response = await fetchWithBQ({
                    url: '/register/28',
                    method: 'POST',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    data: requestPayload,
                });

                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: necessaryEnrichments,
                    });
                    dispatch(tunnelSubscriptionUpdated(user));

                    // we need to clear the BS document list
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendAutoCertification: build.mutation({
            queryFn: async (
                { payload: { filesToRemove, ...payload }, callback },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) => {
                if (filesToRemove?.length > 0) {
                    await documentApi.removeDocuments(filesToRemove);
                }

                const requestPayload = tunnelSchemas.sendAutoCertification(payload);
                const necessaryEnrichments = [
                    ALERTS,
                    COMPANY,
                    DOCUMENTS,
                    KYC_SURVEY,
                    SUBSCRIPTIONS,
                    USER_KNOWLEDGE,
                    USER_TUNNEL_STEPS,
                ];
                const response = await fetchWithBQ({
                    url: '/register/29',
                    method: 'POST',
                    data: requestPayload,
                });

                if (!response.error) {
                    const user = await authApi.getMeEnriched({
                        enrich_with: necessaryEnrichments,
                    });
                    dispatch(tunnelSubscriptionUpdated(user));

                    // we need to clear the BS document list
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    dispatch(tunnelSubscriptionUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendCustomStepData: build.mutation({
            queryFn: async (
                { tunnelStepId, payload },
                { dispatch },
                _extraOptions,
                fetchWithBQ
            ) => {
                const response = await fetchWithBQ({
                    url: `/register/49/${tunnelStepId}`,
                    method: 'POST',
                    data: payload,
                });
                const user = await authApi.getMeEnriched({
                    enrich_with: [
                        USER_TUNNEL_STEPS,
                        SUBSCRIPTIONS,
                        ALERTS,
                        COMPANY,
                        KYC_SURVEY,
                        USER_KNOWLEDGE,
                        DOCUMENTS,
                        INVESTMENT_CHARACTERISTICS,
                    ],
                });
                dispatch(userTunnelStepsUpdated(user));

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendUserKnowledge: build.mutation({
            async queryFn(arg, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const { payload, callback } = arg;

                const response = await fetchWithBQ({
                    url: '/register/35',
                    method: 'POST',
                    data: tunnelSchemas.sendUserKnowledge(payload),
                });

                const necessaryEnrichments = [
                    USER_TUNNEL_STEPS,
                    SUBSCRIPTIONS,
                    USER_KNOWLEDGE,
                    ALERTS,
                    DOCUMENTS,
                    COMPANY,
                    KYC_SURVEY,
                    INVESTMENT_CHARACTERISTICS,
                ];
                const user = await authApi.getMeEnriched({
                    enrich_with: necessaryEnrichments,
                });
                dispatch(tunnelSubscriptionUpdated(user));

                const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                const documentsQueryObject = {
                    type: 'SUBSCRIPTION_FORM',
                    subscriptionId: tunnelSubscription?.id,
                    pagination: false,
                };

                // Clear cached documents list
                dispatch(
                    appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                );

                // Update tunnel subscription and form status
                dispatch(tunnelSubscriptionUpdated(user));
                dispatch(subscriptionFormStatusSet(false));

                callback?.();

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendExperience: build.mutation({
            async queryFn(arg, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const { payload, callback } = arg;

                // Send POST to backend
                const response = await fetchWithBQ({
                    url: '/register/36',
                    method: 'POST',
                    data: tunnelSchemas.sendExperience(payload),
                });

                const status = response.error?.status;
                const isBusinessError = status && ![400, 401, 403, 500].includes(status);

                // Perform enrichment if no error or if business error
                if (!response.error || isBusinessError) {
                    const enrich_with = [
                        INVESTOR_PROFILE,
                        USER_ADEQUACY,
                        USER_TUNNEL_STEPS,
                        KYC_SURVEY,
                        ALERTS,
                        DOCUMENTS,
                        COMPANY,
                        SUBSCRIPTIONS,
                        USER_KNOWLEDGE,
                        INVESTMENT_CHARACTERISTICS,
                    ];

                    const user = await authApi.getMeEnriched({ enrich_with });
                    dispatch(tunnelSubscriptionUpdated(user));

                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());

                    const documentsQueryObject = {
                        type: 'SUBSCRIPTION_FORM',
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };

                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    dispatch(subscriptionFormStatusSet(false));
                }

                if (response.error) {
                    // Business error passed back to component (e.g., 422)
                    return { error: response.error };
                }

                callback?.();

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendUserAdequacy: build.mutation({
            async queryFn(arg, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const { payload, callback } = arg;

                const response = await fetchWithBQ({
                    url: '/register/37',
                    method: 'POST',
                    data: tunnelSchemas.sendUserAdequacy(payload),
                });

                const status = response.error?.status;
                const isBusinessError = status && ![400, 401, 403, 500].includes(status);

                if (!response.error || isBusinessError) {
                    const enrich_with = [
                        INVESTOR_PROFILE,
                        USER_ADEQUACY,
                        USER_TUNNEL_STEPS,
                        KYC_SURVEY,
                        ALERTS,
                        DOCUMENTS,
                        COMPANY,
                        SUBSCRIPTIONS,
                        USER_KNOWLEDGE,
                        INVESTMENT_CHARACTERISTICS,
                    ];

                    const user = await authApi.getMeEnriched({ enrich_with });

                    dispatch(tunnelSubscriptionUpdated(user));

                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    const documentsQueryObject = {
                        type: 'SUBSCRIPTION_FORM',
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };

                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );
                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendRegulatoryDocuments: build.mutation({
            async queryFn(callback, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: '/register/39',
                    method: 'POST',
                });

                if (!response.error) {
                    const enrich_with = [
                        USER_TUNNEL_STEPS,
                        SUBSCRIPTIONS,
                        ALERTS,
                        DOCUMENTS,
                        COMPANY,
                        KYC_SURVEY,
                        USER_KNOWLEDGE,
                    ];

                    const user = await authApi.getMeEnriched({ enrich_with });

                    dispatch(tunnelSubscriptionUpdated(user));

                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    const documentsQueryObject = {
                        type: 'SUBSCRIPTION_FORM',
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };

                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );

                    callback?.();
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        createUserStep: build.mutation({
            query: payload => ({
                url: '/user_tunnel_steps',
                method: 'POST',
                data: tunnelSchemas.createUserStep(payload),
            }),
        }),

        getPhoneVerificationCode: build.mutation({
            query: () => ({
                url: '/users/phone-verification/sms',
                method: 'POST',
            }),
        }),

        checkVerificationCode: build.mutation({
            async queryFn(payload, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: '/users/phone-verification/check',
                    method: 'POST',
                    data: payload,
                });

                if (!response.error) {
                    const enrich_with = [
                        INVESTOR_PROFILE,
                        INVESTMENT_CHARACTERISTICS,
                        INVESTMENT_FUND,
                        USER_TUNNEL_STEPS,
                        SUBSCRIPTIONS,
                        ALERTS,
                        DOCUMENTS,
                        COMPANY,
                        KYC_SURVEY,
                        USER_KNOWLEDGE,
                    ];

                    const user = await authApi.getMeEnriched({ enrich_with });
                    dispatch(userTunnelStepsUpdated(user));
                    dispatch(subscriptionFormStatusSet(false));

                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());
                    dispatch(
                        appApi.endpoints.getTunnels.initiate(
                            {
                                investmentFunds:
                                    tunnelSubscription?.investmentFund?.id || undefined,
                                role: tunnelSubscription?.role?.id || undefined,
                            },
                            { forceRefetch: true }
                        )
                    );
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useSendInvestorProfileMutation,
    useSendKnowClientMutation,
    useStartIdVerificationMutation,
    useStartSecondIdVerificationMutation,
    useSendIdVerificationMutation,
    useStartAddressVerificationMutation,
    useSendAddressVerificationMutation,
    useSendBankAccountMutation,
    useSendRegulationAgreementMutation,
    useSendDiciAgreementMutation,
    useGetDDASigningSessionMutation,
    useCompleteDDASigningSessionMutation,
    useGenerateSubscriptionFormMutation,
    useGetSubscriptionSigningSessionMutation,
    useCompleteSubscriptionSigningSessionMutation,
    useCompleteCoSubscriptionSigningSessionMutation,
    useFinishDistributorTunnelMutation,
    useSendDistributorDocumentsMutation,
    useSendSurveyResultsMutation,
    useConfirmKYCPedagogicalStepMutation,
    useSendInvestorEligibilityMutation,
    useSendKBISMutation,
    useSendStatutsMutation,
    useSendInvestorCompanyMutation,
    useSendCompanyBeneficiariesMutation,
    useSendAutoCertificationMutation,
    useSendCustomStepDataMutation,
    useSendUserKnowledgeMutation,
    useSendExperienceMutation,
    useSendUserAdequacyMutation,
    useSendRegulatoryDocumentsMutation,
    useCreateUserStepMutation,
    useGetPhoneVerificationCodeMutation,
    useCheckVerificationCodeMutation,
} = tunnelApi;
